import { useQuery } from '@tanstack/react-query'
import { useMutation } from '@tanstack/react-query'

import { useSnackbar } from 'notistack'
import {
  deleteData,
  getData,
  postData,
  updateData,
  updateFromData,
} from '../../../../apis/api.helpers'
import apiUrl from '../../../../apis/api.url'
import { QueryParams } from '../../../../common/types'
import { useSnackbarManager } from '../../../../components/common/snackbar'
import {
  getErrorMessage,
  parseQueryParams,
} from '../../../../utilities/parsers'
import { useAuthStore } from '../../../../store/authStore'

const buildUrlWithParams = (baseUrl: string, params: QueryParams) => {
  return `${baseUrl}${parseQueryParams(params)}`
}

const fetchData = async (input: QueryParams) => {
  const url = buildUrlWithParams(apiUrl.ADMIN_INSTITUTIONS, {
    ...input,
  })
  const response = await getData(url)
  return response
}

export const useAdminUser = (input: QueryParams) => {
  return useQuery(['admin_user_list', input], () => fetchData(input), {})
}

const customerMode = async (input: QueryParams) => {
  const url = buildUrlWithParams(apiUrl.CUSTOMER_MODE, {
    ...input,
  })
  const response = await getData(url)
  return response
}

export const useCustomerMode = (input: QueryParams) => {
  return useQuery(['customer_mode', input], () => customerMode(input), {})
}
export const deleteAdmin = (id?: string) => {
  return deleteData(`${apiUrl.ADMIN_INSTITUTIONS}/${id}`)
}

const adminDetails = async (id: string) => {
  const url = `${apiUrl.ADMIN_INSTITUTIONS}/${id}`
  const response = await getData(url)
  return response
}
export const useAdminDetails = (id: string) => {
  return useQuery(['worker_task', id], () => adminDetails(id), {
    refetchOnWindowFocus: false,
  })
}

export const createIncubation = (input: any) => {
  return postData(`${apiUrl.INCUBATION_CREATE}`, input)
}
export const useCreateIncubation = (onSuccessHandler: (data: any) => void) => {
  const { enqueueSnackbar } = useSnackbarManager()
  const authenticated = useAuthStore((state) => state.authenticated)

  return useMutation({
    mutationFn: async (input: any) => {
      if (!authenticated) throw new Error('Not authenticated')
      return createIncubation(input)
    },
    onSuccess: (res: any) => {
      onSuccessHandler(res.data)
      enqueueSnackbar('Academy created successfully.', { variant: 'success' })
    },
    onError: (err: any) => {
      enqueueSnackbar(
        getErrorMessage(err?.response?.data?.message || err?.message),
        {
          variant: 'error',
        }
      )
    },
  })
}
export const createCv = (input: any, id: any) => {
  console.log(input)
  return postData(`${apiUrl.INCUBATION_CREATE}/${id}/upload-pitch-deck`, input)
}
export const useCreateCv = (onSuccessHandler: (data: any) => void) => {
  const { enqueueSnackbar } = useSnackbarManager()
  const authenticated = useAuthStore((state) => state.authenticated)

  return useMutation({
    mutationFn: async ({ formData, id }: { formData: any; id: any }) => {
      if (!authenticated) throw new Error('Not authenticated')
      return createCv(formData, id)
    },
    onSuccess: (res: any) => {
      onSuccessHandler(res.data)
      enqueueSnackbar('CV uploaded successfully.', { variant: 'success' })
    },
    onError: (err: any) => {
      enqueueSnackbar(
        getErrorMessage(err?.response?.data?.message || err?.message),
        {
          variant: 'error',
        }
      )
    },
  })
}

const fetchLearningForm = async (id: any) => {
  const url = `${apiUrl.LEARNING_FORM}/${id}/questions`

  const response = await getData(url)
  return response.data.questions || []
}

const fetchProgramDetails = async (id: any) => {
  const url = `${apiUrl.PROGRAM_DETAILS}/${id}`
  const response = await getData(url)
  return response.data
}

export const useFetchLearningForm = (id: any) => {
  return useQuery(['learning_form_detail', id], () => fetchLearningForm(id), {
    refetchOnWindowFocus: false,
    //staleTime: 50000,
  })
}

export const useFetchProgramDetails = (id: any) => {
  return useQuery(['program_details', id], () => fetchProgramDetails(id), {
    refetchOnWindowFocus: false,
  })
}

const createBooking = async (bookingData: any) => {
  console.log('Creating booking with data:', bookingData)

  // Create FormData for multipart/form-data
  const formData = new FormData()

  // Add simple fields
  formData.append('user_id', bookingData.user_id)
  formData.append('program_id', bookingData.program_id)
  formData.append('fees', bookingData.fees)

  // Add responses with question IDs as keys
  if (bookingData.responses && Object.keys(bookingData.responses).length > 0) {
    Object.keys(bookingData.responses).forEach((questionId: string) => {
      const response = bookingData.responses[questionId]
      if (response instanceof File) {
        // If it's a file, append as binary file with question ID as key
        formData.append(`responses[${questionId}]`, response, response.name)
      } else {
        // If it's a string, append as string with question ID as key
        formData.append(`responses[${questionId}]`, response)
      }
    })
  } else {
    // Add empty responses object if no responses exist
    formData.append('responses', JSON.stringify({}))
  }

  console.log('FormData entries:')
  formData.forEach((value, key) => {
    console.log(key, value)
  })

  const response = await postData(`${apiUrl.BOOKINGS}`, formData)
  console.log('Booking response:', response)
  return response
}

const createEventBooking = async (bookingData: any) => {
  console.log('Creating event booking with data:', bookingData)
  const response = await postData(`${apiUrl.EVENTBOOKINGS}`, bookingData)
  console.log('Event booking response:', response)
  return response
}

export const useCreateEventBooking = (
  onSuccessHandler: (data: any) => void
) => {
  const { enqueueSnackbar } = useSnackbarManager()
  const authenticated = useAuthStore((state) => state.authenticated)

  return useMutation({
    mutationFn: async (bookingData: any) => {
      console.log('Mutation triggered, authenticated:', authenticated)
      return createEventBooking(bookingData)
    },
    onSuccess: (data: any) => {
      const successMessage =
        data?.message ||
        'Successfully booked for event! Admin will contact you soon.'
      enqueueSnackbar(successMessage, {
        variant: 'success',
      })
      onSuccessHandler(data)
    },
    onError: (error: any) => {
      console.error('Mutation error:', error)
      enqueueSnackbar('Failed to book event. Please try again.', {
        variant: 'error',
      })
    },
  })
}

export const useCreateBooking = (onSuccessHandler: (data: any) => void) => {
  const { enqueueSnackbar } = useSnackbarManager()
  const authenticated = useAuthStore((state) => state.authenticated)

  return useMutation({
    mutationFn: async (bookingData: any) => {
      console.log('Mutation triggered, authenticated:', authenticated)
      if (!authenticated) throw new Error('Not authenticated')
      return createBooking(bookingData)
    },
    onSuccess: (res: any) => {
      console.log('Mutation success:', res)
      const successMessage =
        res?.message || 'Successfully registered! Admin will contact you soon.'
      onSuccessHandler(res.data)
      enqueueSnackbar(successMessage, { variant: 'success' })
    },
    onError: (err: any) => {
      console.log('Mutation error:', err)
      enqueueSnackbar(
        getErrorMessage(err?.response?.data?.message || err?.message),
        {
          variant: 'error',
        }
      )
    },
  })
}

export const assignTemplate = (input: any) => {
  return postData(`${apiUrl.TEMPLATE_ASSIGN}`, input)
}
export const useAssignTemplate = (handleSubmission: (data: any) => void) => {
  const { enqueueSnackbar } = useSnackbarManager()
  return useMutation(assignTemplate, {
    onSuccess: (res: any) => {
      handleSubmission(res.data)
      enqueueSnackbar('Template Assigned Successfully', { variant: 'success' })
    },

    onError: (error: any) => {
      enqueueSnackbar(
        getErrorMessage(
          error.response.data.error || error?.response?.data?.detail
        ),
        {
          variant: 'error',
        }
      )
    },
  })
}

export const editCategory = (data: { id: number; input: any }) => {
  return updateData(`${apiUrl.ADMIN_INSTITUTIONS}/${data.id}`, {
    user: data.input,
  })
}

export const useUpdateAdmin = (
  handleSubmission: (data: any) => void // Simplify this if `id` isn't needed separately
) => {
  const { enqueueSnackbar } = useSnackbar()

  return useMutation(editCategory, {
    onSuccess: (res: any) => {
      handleSubmission(res.data)

      enqueueSnackbar('Customer Edited Successfully', { variant: 'success' })
    },
    onError: (error: any) => {
      enqueueSnackbar(getErrorMessage(error?.response?.data?.message), {
        variant: 'error',
      })
    },
  })
}

export const updatePassword = (employee: string, data: string) => {
  return updateFromData(
    `${apiUrl.ADMIN_USER}/${employee}/change_password`,
    data
  )
}

export const sendAdminInvitation = (id?: string) => {
  return postData(`${apiUrl.ADMIN_USER}/${id}/invite`, {})
}
